import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OnboardingQuillEditor from './OnboardingQuillEditor';
import './OnboardingWorkflow.css';

/**
 * Assignee categories for human-managed tasks
 */
const ASSIGNEE_CATEGORIES = [
    { id: 'hr', label: 'HR Team', icon: 'fa-people-group', color: '#4CAF50' },
    { id: 'it', label: 'IT Department', icon: 'fa-laptop-code', color: '#9C27B0' },
    { id: 'new_hire', label: 'New Hire', icon: 'fa-user-plus', color: '#2196F3' },
    { id: 'manager', label: "Hire's Manager", icon: 'fa-user-tie', color: '#FF9800' },
    { id: 'other_dept', label: 'Other Department', icon: 'fa-building', color: '#607D8B' }
];

/**
 * Modal for creating/editing human-managed tasks
 * Supports assignee selection from various categories and scheduled emails
 */
const AddHumanTaskModal = ({
    onClose,
    onSave,
    hire,
    stage,
    teamMembers = [],
    externalContacts = [],
    editingTask = null // If provided, we're editing an existing task
}) => {
  useUiLanguage();
    const { t } = useTranslation('onboarding');
    const isEditing = !!editingTask;
    const normalizeText = (value) => String(value || '').toLowerCase();
    const parseRequiredDocuments = (value) => (
        (value || '')
            .split(/\n|,|;/)
            .map(item => item.trim())
            .filter(Boolean)
    );
    const looksLikeIT = (person) => {
        const haystack = [
            person?.role,
            person?.department,
            person?.title,
            person?.team,
            person?.function
        ].map(normalizeText).join(' ');
        return /(it|information technology|systems|sysadmin|help\s?desk|tech|technology|devops|infrastructure)/.test(haystack);
    };
    const dedupeByEmail = (people = []) => {
        const seen = new Set();
        return people.filter(person => {
            const email = String(person?.email || '').trim().toLowerCase();
            if (!email || seen.has(email)) return false;
            seen.add(email);
            return true;
        });
    };
    const getPersonDepartment = (person = {}, isInternal = false) => {
        if (person.department) return person.department;
        const fallback = person.role || person.title || person.team || person.function || '';
        if (!isInternal) return fallback;
        const normalizedFallback = normalizeText(fallback);
        if (/(owner|collaborator|team member|admin|administrator)/.test(normalizedFallback)) {
            return 'HR';
        }
        return fallback;
    };
    const looksLikeHR = (value) => /(hr|human resources|people ops|people operations|talent)/.test(normalizeText(value));
    const normalizePeople = (people = [], options = {}) => (
        people
            .map(person => ({
                ...person,
                email: String(person?.email || '').trim(),
                name: person?.name || person?.email || 'Unknown',
                department: getPersonDepartment(person, Boolean(options.internal))
            }))
            .filter(person => person.email)
    );
    const allPeopleOptions = dedupeByEmail([
        ...normalizePeople(teamMembers, { internal: true }),
        ...normalizePeople(externalContacts)
    ]).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    const getOptionsForCategory = (category) => {
        switch (category) {
            case 'hr':
                return normalizePeople(teamMembers, { internal: true });
            case 'it':
                return dedupeByEmail([
                    ...normalizePeople(teamMembers.filter(looksLikeIT), { internal: true }),
                    ...normalizePeople(externalContacts.filter(looksLikeIT))
                ]);
            case 'new_hire':
                return hire?.email
                    ? [{ email: hire.email, name: hire.name, department: getPersonDepartment(hire) }]
                    : [];
            case 'manager':
                if (!hire?.manager) return [];
                const manager = teamMembers.find(m => m.email === hire.manager);
                return manager
                    ? normalizePeople([manager], { internal: true })
                    : [{ email: hire.manager, name: hire.manager, department: '' }];
            case 'other_dept':
                return allPeopleOptions;
            default:
                return [];
        }
    };
    const inferAssigneeCategoryForTask = (task) => {
        const fallbackDepartment = String(task?.assigneeDepartment || task?.department || '');
        const email = String(task?.assignee || task?.assigneeEmail || '').trim().toLowerCase();
        const assignee = allPeopleOptions.find(person => String(person.email || '').trim().toLowerCase() === email);
        const department = assignee?.department || fallbackDepartment;

        let inferredCategory = 'other_dept';
        if (email && hire?.email && email === String(hire.email).trim().toLowerCase()) inferredCategory = 'new_hire';
        else if (email && hire?.manager && email === String(hire.manager).trim().toLowerCase()) inferredCategory = 'manager';
        else if (looksLikeIT({ ...assignee, department })) inferredCategory = 'it';
        else if (looksLikeHR(department)) inferredCategory = 'hr';

        const inCategory = getOptionsForCategory(inferredCategory)
            .some(person => String(person.email || '').trim().toLowerCase() === email);
        return inCategory || !email ? inferredCategory : 'other_dept';
    };

    const [form, setForm] = useState(() => {
        // If editing, pre-fill the form with existing task data
        if (editingTask) {
            const requiredDocuments = Array.isArray(editingTask.requiredDocuments) ? editingTask.requiredDocuments : [];
            const inferredCategory = inferAssigneeCategoryForTask(editingTask);
            return {
                title: editingTask.title || '',
                description: editingTask.description || '',
                assigneeCategory: editingTask.assigneeCategory || inferredCategory,
                assigneeEmail: editingTask.assignee || editingTask.assigneeEmail || '',
                assigneeName: editingTask.assigneeName || '',
                assigneeDepartment: editingTask.assigneeDepartment || '',
                emailDraft: {
                    subject: editingTask.emailDraft?.subject || '',
                    body: editingTask.emailDraft?.body || ''
                },
                dueDate: editingTask.dueDate || '',
                requiredDocumentsText: requiredDocuments.join('\n')
            };
        }
        // Default empty form for new task
        return {
            title: '',
            description: '',
            assigneeCategory: '',
            assigneeEmail: '',
            assigneeName: '',
            assigneeDepartment: '',
            emailDraft: { subject: '', body: '' },
            dueDate: '',
            requiredDocumentsText: ''
        };
    });

    const getAssigneeOptions = () => {
        return getOptionsForCategory(form.assigneeCategory);
    };

    const handleCategorySelect = (categoryId) => {
        setForm({
            ...form,
            assigneeCategory: categoryId,
            assigneeEmail: '',
            assigneeName: '',
            assigneeDepartment: ''
        });

        // Auto-select if only one option
        const options = (() => {
            switch (categoryId) {
                case 'new_hire':
                    return hire?.email ? [{ email: hire.email, name: hire.name }] : [];
                case 'manager':
                    if (!hire?.manager) return [];
                    const manager = teamMembers.find(m => m.email === hire.manager);
                    return manager ? [manager] : [{ email: hire.manager, name: hire.manager }];
                default:
                    return [];
            }
        })();

        if (options.length === 1) {
            setForm(prev => ({
                ...prev,
                assigneeCategory: categoryId,
                assigneeEmail: options[0].email,
                assigneeName: options[0].name,
                assigneeDepartment: options[0].department || ''
            }));
        }
    };

    const handleAssigneeSelect = (email) => {
        const options = getAssigneeOptions();
        const selected = options.find(o => o.email === email);
        setForm({
            ...form,
            assigneeEmail: email,
            assigneeName: selected?.name || email,
            assigneeDepartment: selected?.department || ''
        });
    };

    // Email generation removed

    const handleSave = () => {
        if (!form.title.trim()) {
            alert(uiText('Please enter a task title'));
            return;
        }

        if (!form.assigneeEmail) {
            alert(uiText('Please select an assignee'));
            return;
        }

        const taskData = {
            title: form.title,
            description: form.description,
            type: 'human',
            assignee: form.assigneeEmail,
            assigneeName: form.assigneeName,
            assigneeDepartment: form.assigneeDepartment || '',
            assigneeCategory: form.assigneeCategory,
            emailDraft: form.emailDraft,
            dueDate: form.dueDate,
            status: 'not_started'
        };
        const requiredDocuments = parseRequiredDocuments(form.requiredDocumentsText);
        if (requiredDocuments.length > 0) {
            taskData.requiredDocuments = requiredDocuments;
            taskData.documentCollectionMode = 'candidate_upload';
        }

        onSave(taskData, stage);
    };

    const assigneeOptions = getAssigneeOptions();
    const selectedCategory = ASSIGNEE_CATEGORIES.find(c => c.id === form.assigneeCategory);
    const selectedAssignee = assigneeOptions.find(person => person.email === form.assigneeEmail);
    const selectedDepartment = selectedAssignee?.department || form.assigneeDepartment || '';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-onboarding" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEditing ? t('humanTaskModal.editTitle', 'Edit Task') : t('humanTaskModal.createTitle', 'Create Task by Team Member')}</h2>
                    <button className="close" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body-onboarding">
                    {/* Task Title */}
                    <div className="form-group">
                        <label>{t('humanTaskModal.taskTitle', 'Task Title *')}</label>
                        <input
                            type="text"
                            value={form.title}
                            onChange={e => setForm({ ...form, title: e.target.value })}
                            placeholder={t('humanTaskModal.taskTitlePlaceholder', 'e.g., Set up workstation')}
                        />
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label>{t('modals.addTask.description', 'Description')}</label>
                        <textarea
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            placeholder={t('humanTaskModal.descriptionPlaceholder', 'Describe what needs to be done...')}
                            rows={3}
                        />
                    </div>

                    {/* Assignee Category Selection */}
                    <div className="form-group">
                        <label>{t('modals.addTask.assignTo', 'Assign To')} *</label>
                        <div className="assignee-category-grid">
                            {ASSIGNEE_CATEGORIES.map(cat => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    className={`assignee-cat-btn ${form.assigneeCategory === cat.id ? 'active' : ''}`}
                                    onClick={() => handleCategorySelect(cat.id)}
                                    style={{ '--cat-color': cat.color }}
                                >
                                    <i className={`fa-solid ${cat.icon}`} />
                                    <span>{t(`humanTaskModal.categories.${cat.id}`, cat.label)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Assignee Selection (if category selected) */}
                    {form.assigneeCategory && assigneeOptions.length > 0 && (
                        <div className="form-group">
                            <label>{t('humanTaskModal.selectPerson', 'Select Person')}</label>
                            <select
                                value={form.assigneeEmail}
                                onChange={e => handleAssigneeSelect(e.target.value)}
                            >
                                <option value="">{t('humanTaskModal.choosePerson', 'Choose a person...')}</option>
                                {assigneeOptions.map(person => (
                                    <option key={person.email} value={person.email}>
                                        {person.name} ({person.email}){person.department ? ` - ${person.department}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {form.assigneeCategory === 'it' && assigneeOptions.length === 0 && (
                        <div className="form-group">
                            <small style={{ color: 'var(--app-text, #cbd5e1)' }}>
                                {t('humanTaskModal.noItContacts', 'No IT contacts found. Add IT members in External Team or team settings.')}
                            </small>
                        </div>
                    )}

                    {/* Selected Assignee Display */}
                    {form.assigneeEmail && (
                        <div className="selected-assignee-display">
                            <i className={`fa-solid ${selectedCategory?.icon || 'fa-user'}`} />
                            <div>
                                <div className="assignee-name">{form.assigneeName}</div>
                                <div className="assignee-email">{form.assigneeEmail}</div>
                                {selectedDepartment && (
                                    <div className="assignee-department">{t('humanTaskModal.departmentPrefix', 'Department')}: {selectedDepartment}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Due Date */}
                    <div className="form-group">
                        <label>{t('modals.addTask.dueDate', 'Due Date')}</label>
                        <input
                            type="date"
                            value={form.dueDate}
                            onChange={e => setForm({ ...form, dueDate: e.target.value })}
                        />
                    </div>

                    <div className="email-draft-section">
                        <div className="section-header-small">
                            <label>{t('humanTaskModal.emailDraft', 'Email Draft (for assignee notification)')}</label>
                        </div>
                        <div className="form-group">
                            <label>{t('modals.addTask.emailSubject', 'Subject')}</label>
                            <input
                                type="text"
                                value={form.emailDraft.subject}
                                onChange={e => setForm({
                                    ...form,
                                    emailDraft: { ...form.emailDraft, subject: e.target.value }
                                })}
                                placeholder={t('humanTaskModal.emailSubjectPlaceholder', 'Task notification subject...')}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('modals.addTask.emailBody', 'Message')}</label>
                            <OnboardingQuillEditor
                                className="onboarding-quill"
                                value={form.emailDraft.body}
                                onChange={(value) => setForm({
                                    ...form,
                                    emailDraft: { ...form.emailDraft, body: value }
                                })}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{t('humanTaskModal.requiredDocuments', 'Required Documents (optional)')}</label>
                        <textarea
                            value={form.requiredDocumentsText}
                            onChange={e => setForm({ ...form, requiredDocumentsText: e.target.value })}
                            placeholder={t('humanTaskModal.requiredDocumentsPlaceholder', 'One document per line, e.g.\nPassport\nBank Details Form')}
                            rows={3}
                        />
                    </div>

                    {/* Email Scheduling Removed */}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={onClose}>
                        {t('buttons.cancel', 'Cancel')}
                    </button>
                    <button type="button" className="btn-primary" onClick={handleSave}>
                        {isEditing ? t('humanTaskModal.saveTask', 'Save Task') : t('humanTaskModal.createTask', 'Create Task')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddHumanTaskModal;
export { ASSIGNEE_CATEGORIES };
